/* ============================================================
   MOGADORO v3 — Motion engine
   Scroll reveals, 3D tilt, parallax, page-transition fallback,
   hover prefetch, header state. No dependencies. ~4KB min.
   ============================================================ */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = document.documentElement.classList.contains('shopify-design-mode') || window.Shopify && window.Shopify.designMode;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ----------------------------------------------------------
     1. Page transitions — fallback for browsers without the
        cross-document View Transitions API.
     ---------------------------------------------------------- */
  var hasVT = 'CSSViewTransitionRule' in window || (window.CSS && CSS.supports && CSS.supports('view-transition-name: root'));

  if (!hasVT && !reducedMotion && !designMode) {
    document.documentElement.classList.add('mg-no-vt');

    var ready = function () {
      document.documentElement.classList.add('mg-page-ready');
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', ready);
    } else {
      ready();
    }

    // restore state when returning via back/forward cache
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        document.documentElement.classList.remove('mg-page-exit');
        document.documentElement.classList.add('mg-page-ready');
      }
    });

    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      if (a.target && a.target !== '_self') return;
      if (a.hasAttribute('download')) return;
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      var url;
      try { url = new URL(a.href, location.href); } catch (err) { return; }
      if (url.origin !== location.origin) return;
      if (url.pathname === location.pathname && url.hash) return;
      if (/\/(cart|checkout|account)/.test(url.pathname)) return;

      e.preventDefault();
      document.documentElement.classList.add('mg-page-exit');
      setTimeout(function () { location.href = a.href; }, 180);
    });
  }

  /* ----------------------------------------------------------
     2. Hover prefetch — warms next page while the cursor
        hesitates over a link. Dedup'd, capped, same-origin.
     ---------------------------------------------------------- */
  var prefetched = {};
  var prefetchCount = 0;

  function prefetch(href) {
    if (prefetched[href] || prefetchCount > 20) return;
    var url;
    try { url = new URL(href, location.href); } catch (err) { return; }
    if (url.origin !== location.origin) return;
    if (/\/(cart|checkout|account|challenge)/.test(url.pathname)) return;
    prefetched[href] = true;
    prefetchCount++;
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url.pathname + url.search;
    document.head.appendChild(link);
  }

  if (!designMode) {
    document.addEventListener('mouseover', function (e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (a && a.href) prefetch(a.href);
    }, { passive: true });
    document.addEventListener('touchstart', function (e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (a && a.href) prefetch(a.href);
    }, { passive: true });
  }

  /* ----------------------------------------------------------
     3. Scroll-reveal choreography
        Targets custom Mogadoro sections + product grids.
        Skips anything Dawn already animates (.scroll-trigger).
     ---------------------------------------------------------- */
  var revealSelectors = [
    '.mcat__item',
    '.acard',
    '.mstory__content',
    '.mstat__item',
    '.mtile',
    '.mfact',
    '.mmat__body',
    '.mig__item',
    '.mwhole__inner',
    '.mcat__head',
    '.artisans__head',
    '.multicolumn-card',
    '.collection-list__item',
    '#product-grid .grid__item',
    '.footer-block'
  ].join(',');

  var mediaSelectors = [
    '.mstory__media',
    '.image-with-text__media-item'
  ].join(',');

  function initReveals() {
    if (reducedMotion || designMode || !('IntersectionObserver' in window)) return;

    var targets = [];
    document.querySelectorAll(revealSelectors).forEach(function (el) {
      if (!el.closest('.scroll-trigger')) targets.push({ el: el, media: false });
    });
    document.querySelectorAll(mediaSelectors).forEach(function (el) {
      if (!el.closest('.scroll-trigger')) targets.push({ el: el, media: true });
    });

    if (!targets.length) return;

    // stagger: index within shared parent, capped at 6
    var parentCounts = new Map();
    targets.forEach(function (t) {
      var p = t.el.parentElement;
      var i = parentCounts.get(p) || 0;
      t.el.style.setProperty('--mg-i', Math.min(i, 6));
      parentCounts.set(p, i + 1);
      t.el.classList.add('mg-reveal');
      if (t.media) t.el.classList.add('mg-reveal--media');
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('mg-inview');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    targets.forEach(function (t) { io.observe(t.el); });
  }

  /* ----------------------------------------------------------
     4. 3D tilt cards — desktop only, rAF-throttled
     ---------------------------------------------------------- */
  function initTilt() {
    if (reducedMotion || !finePointer) return;

    var cards = document.querySelectorAll('.card-wrapper, .mcat__card, .acard');
    var MAX = 4; // degrees

    cards.forEach(function (card) {
      card.classList.add('mg-tilt');

      var sheen = document.createElement('span');
      sheen.className = 'mg-sheen';
      sheen.setAttribute('aria-hidden', 'true');
      card.appendChild(sheen);

      var raf = null;
      var rect = null;

      card.addEventListener('pointerenter', function () {
        rect = card.getBoundingClientRect();
        card.classList.add('mg-tilting');
      });

      card.addEventListener('pointermove', function (e) {
        if (!rect) rect = card.getBoundingClientRect();
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = null;
          var px = (e.clientX - rect.left) / rect.width;   // 0..1
          var py = (e.clientY - rect.top) / rect.height;   // 0..1
          card.style.setProperty('--mg-ry', ((px - 0.5) * 2 * MAX).toFixed(2) + 'deg');
          card.style.setProperty('--mg-rx', ((0.5 - py) * 2 * MAX).toFixed(2) + 'deg');
          card.style.setProperty('--mg-mx', (px * 100).toFixed(1) + '%');
          card.style.setProperty('--mg-my', (py * 100).toFixed(1) + '%');
        });
      });

      card.addEventListener('pointerleave', function () {
        rect = null;
        card.classList.remove('mg-tilting');
        card.style.setProperty('--mg-rx', '0deg');
        card.style.setProperty('--mg-ry', '0deg');
      });
    });
  }

  /* ----------------------------------------------------------
     5. Parallax — hero + story media drift on scroll
     ---------------------------------------------------------- */
  function initParallax() {
    if (reducedMotion || window.matchMedia('(max-width: 749px)').matches) return;

    var nodes = document.querySelectorAll('.mhero__media, .mstory__frame');
    if (!nodes.length) return;

    var items = [];
    nodes.forEach(function (el) {
      el.classList.add('mg-parallax');
      items.push({ el: el, visible: false, depth: el.classList.contains('mhero__media') ? 0.12 : 0.07 });
    });

    // first hero gets the cinematic settle
    var hero = document.querySelector('.mhero');
    if (hero) hero.classList.add('mg-hero-anim');

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var item = items.find(function (i) { return i.el === entry.target; });
        if (item) item.visible = entry.isIntersecting;
      });
    }, { rootMargin: '10% 0px 10% 0px' });
    items.forEach(function (i) { io.observe(i.el); });

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        var vh = window.innerHeight;
        items.forEach(function (item) {
          if (!item.visible) return;
          var r = item.el.getBoundingClientRect();
          var center = r.top + r.height / 2;
          var offset = (center - vh / 2) * -item.depth;
          item.el.style.setProperty('--mg-par', offset.toFixed(1) + 'px');
        });
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ----------------------------------------------------------
     5b. Stat count-up — numbers rise to their value in view
     ---------------------------------------------------------- */
  function initCountUp() {
    if (reducedMotion || !('IntersectionObserver' in window)) return;

    var nums = document.querySelectorAll('.mstat__num');
    if (!nums.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        var el = entry.target;
        var raw = el.textContent.trim();
        var match = raw.match(/^([^0-9]*)([0-9][0-9.,]*)(.*)$/);
        if (!match) return;
        var prefix = match[1];
        var suffix = match[3];
        var numStr = match[2];
        var target = parseFloat(numStr.replace(/,/g, ''));
        if (isNaN(target)) return;
        var decimals = (numStr.split('.')[1] || '').length;
        var useComma = numStr.indexOf(',') !== -1;
        var start = null;
        var DUR = 1400;

        function fmt(v) {
          var s = v.toFixed(decimals);
          if (useComma) s = s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          return s;
        }
        function step(ts) {
          if (!start) start = ts;
          var p = Math.min((ts - start) / DUR, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = prefix + fmt(target * eased) + suffix;
          if (p < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      });
    }, { threshold: 0.5 });

    nums.forEach(function (el) { io.observe(el); });
  }

  /* ----------------------------------------------------------
     6. Header shadow once scrolled
     ---------------------------------------------------------- */
  function initHeader() {
    var header = document.querySelector('.section-header');
    if (!header) return;
    var last = false;
    window.addEventListener('scroll', function () {
      var scrolled = window.scrollY > 24;
      if (scrolled !== last) {
        last = scrolled;
        header.classList.toggle('mg-scrolled', scrolled);
      }
    }, { passive: true });
  }

  /* ----------------------------------------------------------
     boot
     ---------------------------------------------------------- */
  function boot() {
    initReveals();
    initTilt();
    initParallax();
    initCountUp();
    initHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // re-run inside the theme editor when sections reload
  if (designMode) {
    document.addEventListener('shopify:section:load', function () {
      initTilt();
    });
  }
})();
